import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { EquipmentService } from '@app/core/services/equipment.service';
import { ToolService } from '@app/core/services/tool.service';
import { ToolPropertyValidationState } from '@app/tool/model/tool-property-validation-state.enum';
import { ToolValidationConflictTool } from '@app/tool/model/tool-validation-conflict-tool.model';
import * as _ from 'lodash';

import { Log } from "ng2-logger";
import { PartNumbersService } from "@services/part-numbers.service";
import { Tool } from "@app/tool/model/tool.model";
import { WizardModalComponent } from '@app/shared/components';
import { PartNumber } from '@models/partNumber.model';
import { Line } from '@models/line.model';
import { Equipment } from '@models/equipment.model';
import { InsertionCyclesService } from '@app/core/services/insertion-cycles.service';
import { InsertionCycle } from '@app/shared/models/insertion-cycle.model';
import { LineService } from "@services/line.service";
import { ToolMainLineFormComponent } from "@app/tool/tool-form-modal/forms/tool-main-line-form/tool-main-line-form.component";
import { ToastsManager } from '@app/shared/util/toastr.service';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { ToolProperty } from '@app/tool/model/enums/tool-property.enum';
import { ToolPropertyValidationInfoModalComponent } from '@app/tool/components/tool-property-validation-info-modal/tool-property-validation-info-modal.component';
import { isAutoGeneratedToolCodeCodingType, isNACodingType, normalizeCodingTypeValue } from '@app/shared/models/enums/codingType.enum';

enum ToolSteps {
    MAIN_LINE = 0,
    PART_NUMBERS = 1,
    MAIN_EQUIPMENTS = 2,
    INSERTION_CYCLES = 3
}

@Component({
    selector: 'app-tool-config-wizard',
    templateUrl: 'tool-config-wizard.component.html',
    styleUrls: ['tool-config-wizard.component.scss']
})
export class ToolConfigWizardComponent implements AfterViewInit {
    _logger = Log.create(ToolConfigWizardComponent.name);

    @ViewChild(WizardModalComponent, {static: true}) wizardModal: WizardModalComponent;
    @ViewChild('infoModal', { static: true }) infoModal: ToolPropertyValidationInfoModalComponent;

    @ViewChild(ToolMainLineFormComponent, {static: false}) toolMainLineFormComponent: ToolMainLineFormComponent;

    isLoading;
    @Output() onClose = new EventEmitter();

    tool: any = {};

    showSPI = true;

    availableLines = new Array<any>();
    availableParts = new Array<any>();
    availableInsertionCycles = new Array<any>();
    availableMainEquipments = new Array<Equipment>();

    selectedStep: number;
    infoModalTools: ToolValidationConflictTool[] = [];
    private pendingToolDTO: any = null;

    private static toDTO(tool) {
        tool.partNumberIds = tool.partNumbers.map(pn => pn.id);
        tool.equipmentIds = tool.mainEquipment.map(eq => eq.id);
        tool.insertionCycleIds = tool.insertionCycles.map(ic => ic.id);
        tool.lineId = tool?.selectedMainLine?.id;

        const toolDTO: any = Object.assign({}, tool);
        delete toolDTO.partNumbers;
        delete toolDTO.mainEquipment;
        delete toolDTO.insertionCycles;
        delete toolDTO.selectedMainLine;

        return toolDTO;
    }

    constructor(private equipmentService: EquipmentService,
                private partService: PartNumbersService,
                private cdr: ChangeDetectorRef,
                private toolService: ToolService,
                private lineService: LineService,
                private insertionCyclesService: InsertionCyclesService,
                private toastr: ToastsManager,
                private translateService: TranslateService) {
    }

    onStepChange(stepIndex) {
        this.selectedStep = stepIndex;
        switch (stepIndex) {
            case ToolSteps.MAIN_LINE:
                break;
            case ToolSteps.PART_NUMBERS:
                this.tool.selectedMainLine = this.toolMainLineFormComponent?.value?.line ?? this.tool.selectedMainLine;
                this.showSPI = this.tool.selectedMainLine.isSPI;

                this.getPartNumbersByLine(this.tool.selectedMainLine);

                break;
            case ToolSteps.MAIN_EQUIPMENTS:
                this.getEquipmentsByPartNumbers(this.tool.partNumbers);
                break;
            case ToolSteps.INSERTION_CYCLES:
                this.getInsertionCyclesByLineAndByPartNumbers(this.tool.selectedMainLine.id, this.tool.partNumbers);
                break;
        }
    }

    onWizardSubmit() {
        if (this.selectedStep === ToolSteps.INSERTION_CYCLES ||
            (this.selectedStep === ToolSteps.MAIN_EQUIPMENTS && !this.tool.selectedMainLine.isSPI)) {
            this.isLoading = true;
            this._logger.info("onWizardSubmit:: Raw submitted tool", this.tool);

            const toolDTO = ToolConfigWizardComponent.toDTO(this.tool);
            this.validateToolCodeForEquipmentUpdate(toolDTO.equipmentIds)
                .subscribe((result) => {
                    if (result?.validationState === ToolPropertyValidationState.Error) {
                        this.isLoading = false;
                        this.toastr.error(this.translateService.instant('form.validations.toolCodeNotUnique', {
                            tools: this.buildToolsLabel(result.tools || [])
                        }));
                        return;
                    }

                    if (result?.validationState === ToolPropertyValidationState.Info) {
                        this.pendingToolDTO = toolDTO;
                        this.infoModalTools = result.tools || [];
                        this.isLoading = false;
                        this.infoModal.open();
                        return;
                    }

                    this.submitToolUpdate(toolDTO);
                }, () => {
                    this.isLoading = false;
                });
        }
    }

    onWizardCancel() {
        this.reset();
        this.close();
        this.toolService.getTools();
    }

    open(tool: Tool) {
        this.getLines();

        this.tool.id = tool.id;
        this.tool.status = tool.status;
        this.tool.isInStorage = tool.isInStorage;
        this.tool.mesCode = tool.mesCode;
        this.tool.codingType = normalizeCodingTypeValue(tool.codingType);

        this.tool.partNumbers = tool.details.partNumbers.map(pn => {
            return new PartNumber(pn);
        });
        this.tool.mainEquipment = tool.details.equipment.map(e => {
            return new Equipment(e);
        });
        this.tool.insertionCycles = tool.details.insertionCycles.map(ic => {
            return new InsertionCycle(ic);
        });

        this.tool.selectedMainLine = tool.details.mainLine;
        this.showSPI = this.tool.selectedMainLine.isSPI;

        this.wizardModal.open();
    }

    close() {
        this.reset();
        this.wizardModal.close();
        this.onClose.emit(this.tool);
    }

    reset() {
        this.tool = {};

        this.availableParts = [];
        this.availableLines = [];
        this.availableMainEquipments = [];
        this.availableInsertionCycles = [];

        this.wizardModal.reset();
    }

    get isToolInLiftAndStanceNull(): boolean {
        return this.tool?.isInStorage === true && (!this.tool?.stance || this.tool?.stance.trim() === '');
    }

    private getPartNumbersByLine(line: Line) {
        this.isLoading = true;
        this.partService.getPartNumbersByLine(line).subscribe((data) => {
            this.availableParts = data.items;
            this.isLoading = false;

            _.remove(this.tool.partNumbers, (part) => {
                return !_.some(this.availableParts, (availablePart) => availablePart.id === (<any>part).id)
            });
        });
    }

    private getEquipmentsByPartNumbers(partNumbers: PartNumber[]) {
        if (partNumbers.length === 0) {
            this.tool.mainEquipment = [];
            this.availableMainEquipments = [];
            return;
        }
        this.isLoading = true;
        this.equipmentService.getEquipmentByPartNumbers(partNumbers).subscribe((data) => {
            this.availableMainEquipments = data.equipment;
            this.isLoading = false;

            _.remove(this.tool.mainEquipment, (mainEquip) => {
                return !_.some(this.availableMainEquipments, (availableMainEquip) => availableMainEquip.id === (<any>mainEquip).id)
            });
        });
    }

    private getInsertionCyclesByLineAndByPartNumbers(lineId: number, partNumbers: PartNumber[]) {
        if (partNumbers.length === 0) {
            this.tool.insertionCycles = [];
            this.availableInsertionCycles = [];
            return;
        }

        this.isLoading = true;
        this.insertionCyclesService.getInsertionCyclesByLineAndByPartNumbers(lineId, partNumbers).subscribe((data) => {
            this.availableInsertionCycles = data.items || [];
            this.isLoading = false;

            _.remove(this.tool.insertionCycles, (ic) => {
                return !_.some(this.availableInsertionCycles, (availableIC) => availableIC.id === (<any>ic).id)
            });
        });
    }

    ngAfterViewInit(): void {
        this.cdr.markForCheck();
    }

    private getLines() {
        this.isLoading = true;
        this.lineService.getAllLines().subscribe((data) => {
            this.isLoading = false;
            this.availableLines = data.items;
        });
    }

    private validateToolCodeForEquipmentUpdate(equipmentIds: number[]) {
        const mesCode = this.tool?.mesCode?.trim();
        const codingType = normalizeCodingTypeValue(this.tool?.codingType);
        const normalizedEquipmentIds = (equipmentIds || []).filter(id => !!id);

        if (!mesCode || isNACodingType(codingType) || isAutoGeneratedToolCodeCodingType(codingType) || !normalizedEquipmentIds.length) {
            return of(null);
        }

        return this.toolService.validateToolProperty(ToolProperty.MESCode, mesCode, {
            codingType,
            equipmentIds: normalizedEquipmentIds,
            toolId: this.tool.id
        });
    }

    onInfoConfirm() {
        if (!this.pendingToolDTO) {
            return;
        }

        const toolDTO = this.pendingToolDTO;
        this.pendingToolDTO = null;
        this.infoModalTools = [];
        this.isLoading = true;
        this.submitToolUpdate(toolDTO);
    }

    private submitToolUpdate(toolDTO: any) {
        this._logger.info("onWizardSubmit:: Updating tool [DTO]", JSON.stringify(toolDTO));
        this.toolService.updateTool(toolDTO).subscribe((data) => {
            this._logger.info("onWizardSubmit:: API Returned data", data);
            this.toolService.announceToolUpdate(toolDTO);
            this.wizardModal.close();
            this.reset();
            this.isLoading = false;
        }, () => {
            this.isLoading = false;
        });
    }

    private buildToolsLabel(tools: ToolValidationConflictTool[]): string {
        return tools.map(tool => `${tool.toolUid} - ${tool.name}`).join(', ');
    }
}
